import {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

export interface GetOTPInput {
  email: string;
}
export interface LoginOTPInput {
  provider: string;
  authData: {
    email: string;
    code: number;
    csrfToken: string;
  };
}
export interface ChangePasswordInput {
  password: string;
  code: number;
  csrfToken: string;
}
const instance = axios.create();

// generate otp
export const appControllerGetOTP = (
  getOTPInput: GetOTPInput,
  options?: AxiosRequestConfig
): Promise<any> => {
  return axios.post(`/otps/generate`, getOTPInput, options);
};

export const getAppControllerGetOTPMutationOptions = <
  TError = AxiosError<any>,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof appControllerGetOTP>>,
    TError,
    { data: GetOTPInput },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof appControllerGetOTP>>,
  TError,
  { data: GetOTPInput },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof appControllerGetOTP>>,
    { data: GetOTPInput }
  > = (props) => {
    const { data } = props ?? {};

    return appControllerGetOTP(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AppControllerGetOTPMutationResult = NonNullable<
  Awaited<ReturnType<typeof appControllerGetOTP>>
>;
export type AppControllerGetOTPMutationBody = GetOTPInput;
export type AppControllerGetOTPMutationError = AxiosError<any>;

export const useGetOTP = <
  TError = AxiosError<any>,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof appControllerGetOTP>>,
    TError,
    { data: GetOTPInput },
    TContext
  >;
  axios?: AxiosRequestConfig;
}) => {
  const mutationOptions = getAppControllerGetOTPMutationOptions(options);

  return useMutation(mutationOptions);
};

// login
export const appControllerLoginOTP = (
  getOTPInput: LoginOTPInput,
  options?: AxiosRequestConfig
): Promise<any> => {
  return instance.post(`/auth/login`, getOTPInput, options);
};

export const getAppControllerLoginOTPMutationOptions = <
  TError = AxiosError<any>,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof appControllerLoginOTP>>,
    TError,
    { data: LoginOTPInput },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof appControllerLoginOTP>>,
  TError,
  { data: LoginOTPInput },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof appControllerLoginOTP>>,
    { data: LoginOTPInput }
  > = (props) => {
    const { data } = props ?? {};

    return appControllerLoginOTP(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AppControllerLoginOTPMutationResult = NonNullable<
  Awaited<ReturnType<typeof appControllerLoginOTP>>
>;
export type AppControllerLoginOTPMutationBody = LoginOTPInput;
export type AppControllerLoginOTPMutationError = AxiosError<any>;

export const useLoginOTP = <
  TError = AxiosError<any>,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof appControllerLoginOTP>>,
    TError,
    { data: LoginOTPInput },
    TContext
  >;
  axios?: AxiosRequestConfig;
}) => {
  const mutationOptions = getAppControllerLoginOTPMutationOptions(options);

  return useMutation(mutationOptions);
};

// change password
export const appControllerChangePassword = (
  meInput: ChangePasswordInput,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
  return axios.put(`/users/password`, meInput, options);
};

export const getAppControllerChangePasswordMutationOptions = <
  TError = AxiosError<any>,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof appControllerChangePassword>>,
    TError,
    { data: ChangePasswordInput },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof appControllerChangePassword>>,
  TError,
  { data: ChangePasswordInput },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof appControllerChangePassword>>,
    { data: ChangePasswordInput }
  > = (props) => {
    const { data } = props ?? {};

    return appControllerChangePassword(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AppControllerChangePasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof appControllerChangePassword>>
>;
export type AppControllerChangePasswordMutationBody = ChangePasswordInput;
export type AppControllerChangePasswordMutationError = AxiosError<any>;

export const useChangePassword = <
  TError = AxiosError<any>,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof appControllerChangePassword>>,
    TError,
    { data: ChangePasswordInput },
    TContext
  >;
  axios?: AxiosRequestConfig;
}) => {
  const mutationOptions =
    getAppControllerChangePasswordMutationOptions(options);

  return useMutation(mutationOptions);
};

export const appControllerChangePasswordV2 = (
  meInput: ChangePasswordInput,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
  return instance.put(`/users/password`, meInput, options);
};

export const getAppControllerChangePasswordV2MutationOptions = <
  TError = AxiosError<any>,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof appControllerChangePasswordV2>>,
    TError,
    { data: ChangePasswordInput },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof appControllerChangePasswordV2>>,
  TError,
  { data: ChangePasswordInput },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof appControllerChangePasswordV2>>,
    { data: ChangePasswordInput }
  > = (props) => {
    const { data } = props ?? {};

    return appControllerChangePasswordV2(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AppControllerChangePasswordV2MutationResult = NonNullable<
  Awaited<ReturnType<typeof appControllerChangePasswordV2>>
>;
export type AppControllerChangePasswordV2MutationBody = ChangePasswordInput;
export type AppControllerChangePasswordV2MutationError = AxiosError<any>;

export const useChangePasswordV2 = <
  TError = AxiosError<any>,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof appControllerChangePasswordV2>>,
    TError,
    { data: ChangePasswordInput },
    TContext
  >;
  axios?: AxiosRequestConfig;
}) => {
  const mutationOptions =
    getAppControllerChangePasswordV2MutationOptions(options);

  return useMutation(mutationOptions);
};

export const appControllerGetMe = (
  options?: AxiosRequestConfig
): Promise<any> => {
  return instance.get(`/auth/userinfo`, options);
};

export const getAppControllerGetMeQueryKey = () => {
  return [`/auth/userinfo`] as const;
};

export const getAppControllerGetMeQueryOptions = <
  TData = Awaited<ReturnType<typeof appControllerGetMe>>,
  TError = AxiosError<any>
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof appControllerGetMe>>,
    TError,
    TData
  >;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAppControllerGetMeQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof appControllerGetMe>>
  > = ({ signal }) => appControllerGetMe({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof appControllerGetMe>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type AppControllerGetMeQueryResult = NonNullable<
  Awaited<ReturnType<typeof appControllerGetMe>>
>;
export type AppControllerGetMeQueryError = AxiosError<any>;

export const useGetMe = <
  TData = Awaited<ReturnType<typeof appControllerGetMe>>,
  TError = AxiosError<any>
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof appControllerGetMe>>,
    TError,
    TData
  >;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getAppControllerGetMeQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
